import React, { useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import Button from '../../components/Button/Button';
import { useToast } from '../../components/Toast/ToastContext';
import { trackAccount501c3Uploaded, trackAccount501c3Rejected, trackAccount501c3UploadFailed } from '../../analytics';
import {
  rescueCurrent501c3DocumentQuery,
  rescueGet501c3UploadUrlQuery,
  rescueSubmit501c3DocumentMutation,
  type RescueCurrent501c3DocumentData,
  type RescueGet501c3UploadUrlData,
  type RescueSubmit501c3DocumentData,
  type RescueSubmit501c3DocumentVariables,
} from '../../lib/documentQueries';
import AccountSectionCard from './AccountSectionCard';
import styles from './Account.module.scss';

const MAX_FILE_BYTES = 20 * 1024 * 1024;

// MIME type sent on the S3 PUT. Must match what the presigned URL was signed
// with (`APPLICATION_PDF` → `application/pdf`) or S3 rejects the upload.
const PDF_CONTENT_TYPE = 'application/pdf';
const UPLOAD_CONTENT_TYPE = 'APPLICATION_PDF';

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Coarse bucket for an upload failure, so the analytics event stays a small
 * enumerable set rather than free-text error strings. Mirrors the CSV import's
 * taxonomy in `ImportPetsModal`.
 */
function categorizeUploadError(err: unknown): string {
  const message = err instanceof Error ? err.message.toLowerCase() : '';
  if (message.includes('failed to fetch') || message.includes('networkerror')) {
    return 'network_error';
  }
  if (message.includes('forbidden') || message.includes('403')) {
    return 'forbidden';
  }
  return 'unknown';
}

/**
 * 501c3 determination-letter upload → pending review → approved / rejected by
 * the Fi team. Rendered for every rescue portal user (see Account.tsx).
 *
 * Upload is the same three-step handoff the CSV import uses: fetch a presigned
 * PUT URL, PUT the bytes straight to S3, then hand the object key to the
 * backend to file for review. See `lib/documentQueries.ts`.
 */
export default function Card501c3() {
  const { showToast } = useToast();
  const client = useApolloClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data, loading } = useQuery<RescueCurrent501c3DocumentData>(rescueCurrent501c3DocumentQuery);
  const doc = data?.rescueCurrent501c3Document ?? null;

  const [submitDocument] = useMutation<RescueSubmit501c3DocumentData, RescueSubmit501c3DocumentVariables>(
    rescueSubmit501c3DocumentMutation,
    {
      // The mutation supersedes whatever row was current, so the single-document
      // query has to be refetched rather than merged into — Apollo can't know
      // the new row replaces the old one at a different id.
      refetchQueries: [{ query: rescueCurrent501c3DocumentQuery }],
      awaitRefetchQueries: true,
    },
  );

  function handlePickFile() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file after a rejection.
    e.target.value = '';
    if (!file) {
      return;
    }
    const isPdf = file.type === PDF_CONTENT_TYPE || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      trackAccount501c3Rejected('not_pdf');
      setFileError('Please upload a PDF file.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      trackAccount501c3Rejected('size_exceeded');
      setFileError('That file is over 20MB — please upload a smaller PDF.');
      return;
    }

    setFileError(null);
    setUploading(true);
    try {
      // Step 1: presigned PUT URL. `fetchPolicy: 'no-cache'` so we never replay
      // a previously-cached URL whose expiration has elapsed.
      const { data: urlData, error } = await client.query<RescueGet501c3UploadUrlData>({
        query: rescueGet501c3UploadUrlQuery,
        variables: { input: { contentType: UPLOAD_CONTENT_TYPE } },
        fetchPolicy: 'no-cache',
      });
      if (error) {
        throw error;
      }
      const upload = urlData?.rescueGet501c3UploadUrl;
      if (!upload?.url) {
        setFileError('Could not start the upload — please try again.');
        trackAccount501c3UploadFailed('empty_upload_url');
        return;
      }

      // Step 2: PUT the bytes straight to S3. The signed URL signs only `host`,
      // so the request must not carry an `x-amz-*` header the signature doesn't
      // cover — S3 403s on an unsigned one. `Content-Type` isn't `x-amz-*`, so
      // S3 allows it and matches it against the signed content type.
      const putRes = await fetch(upload.url, {
        method: 'PUT',
        headers: { 'Content-Type': PDF_CONTENT_TYPE },
        body: file,
      });
      if (!putRes.ok) {
        setFileError(`Upload failed (HTTP ${putRes.status}) — please try again.`);
        trackAccount501c3UploadFailed(`s3_put_status_${putRes.status}`);
        return;
      }

      // Step 3: file it for review. The refetch above refreshes the card into
      // its pending state.
      await submitDocument({ variables: { input: { uploadedFileKey: upload.key, fileName: file.name } } });

      trackAccount501c3Uploaded(file.size);
      showToast('501c3 uploaded — pending review by the Fi team');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Upload failed';
      setFileError(detail);
      trackAccount501c3UploadFailed(categorizeUploadError(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <AccountSectionCard
      title="501c3 Verification"
      description="Confirms your nonprofit status so Fi can keep your tax-exempt ordering and nonprofit pricing active."
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {loading && !doc ? (
        <div className={styles.loading}>Loading…</div>
      ) : !doc ? (
        <div className={styles.uploadEmpty}>
          <div className={styles.uploadIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className={styles.uploadTitle}>Upload your 501c3 determination letter</div>
          <div className={styles.uploadSub}>
            PDF up to 20MB. The Fi team reviews and verifies it within a few business days.
          </div>
          <Button variant="green" size="sm" onClick={handlePickFile} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload PDF'}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.fileRow}>
            <div className={styles.fileDoc}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className={styles.fileMeta}>
              <div className={styles.fileName}>{doc.originalFileName}</div>
              <div className={styles.fileSize}>
                {formatFileSize(doc.sizeBytes)} · uploaded {formatDate(doc.uploadedAt)}
              </div>
            </div>
            <button type="button" className={styles.replaceBtn} onClick={handlePickFile} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Replace'}
            </button>
          </div>

          {doc.status === 'PENDING' && (
            <span className={`${styles.vbadge} ${styles.vbadgePending}`}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Pending review by the Fi team
            </span>
          )}

          {doc.status === 'APPROVED' && (
            <span className={`${styles.vbadge} ${styles.vbadgeApproved}`}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              Verified 501c3{doc.reviewedAt ? ` · Approved ${formatDate(doc.reviewedAt)}` : ''}
            </span>
          )}

          {doc.status === 'REJECTED' && (
            <>
              <span className={`${styles.vbadge} ${styles.vbadgeRejected}`}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                Not accepted{doc.reviewedAt ? ` · Reviewed ${formatDate(doc.reviewedAt)}` : ''}
              </span>
              <div className={styles.rejectionNote}>
                {doc.rejectionReason
                  ? `The Fi team couldn't verify this document: ${doc.rejectionReason}`
                  : "The Fi team couldn't verify this document."}{' '}
                Upload a replacement to try again.
              </div>
            </>
          )}
        </>
      )}

      {fileError && <div className={styles.uploadError}>{fileError}</div>}
    </AccountSectionCard>
  );
}
